import { type JSX } from 'react';
import { Link } from '@/react/components/core-ui/Link';
import type { SearchBoxVM } from '@/react/view-models/SearchBoxVM';
import type { VoidCallback } from '@/type-utils';
import { Decision } from '../../personalization/Decision';
import { DynamicExperience } from '../../personalization/DynamicExperience';
import { WithConfigEnabled } from '../../utility/WithConfigEnabled';

interface IABSuggestedSearchTermsProps {
  /**
   * The `SearchBoxVM` instance to be used.
   */
  vm: SearchBoxVM;

  /**
   * The function to be called when the user navigates to a new page.
   */
  onNavigate?: VoidCallback;

  /**
   * Classname used to style the suggestion term.
   */
  termClassName: string;
}

/**
 * Renders suggested search terms for an A/B personalization experience,
 * displaying term links that navigate to search results.
 */
const ABSuggestedTerms = WithConfigEnabled(
  (props: IABSuggestedSearchTermsProps) => {
    const { onNavigate, vm, termClassName } = props;

    const renderTerms = (
      decisionProps: { terms: Array<string> } | null
    ): Array<JSX.Element> | null => {
      if (!decisionProps) return null;
      return decisionProps.terms.map((term) => (
        <Link
          key={term}
          className={termClassName}
          href={vm.encodeSearchResultsPageNavigationUrl(term)}
          onClick={onNavigate}
          variant="text"
        >
          {term}
        </Link>
      ));
    };

    return (
      <DynamicExperience
        experienceID="ab-suggested-terms-experience"
        fallback={<div />}
      >
        <Decision id="ab-suggested-term-decision-a">{renderTerms}</Decision>
        <Decision id="ab-suggested-term-decision-b">{renderTerms}</Decision>
      </DynamicExperience>
    );
  },
  'search',
  'ABSuggestedTerms.enabled'
);

export default ABSuggestedTerms;
